import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Observable, Subject, BehaviorSubject, of, forkJoin } from 'rxjs';
import { takeUntil, catchError, finalize } from 'rxjs/operators';

// Services
import { AuthService } from '../../services/auth.service';
import { OrderService } from '../../services/order.service';
import { PreAlertService } from '../../services/pre-alert.service';

// Spartan / ng-icons
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import {
  lucideArrowLeft, lucideRefreshCw, lucidePackage, lucideBell, lucideClock,
  lucideCheckCircle, lucideZap, lucideUpload, lucideScanLine, lucideActivity,
  lucideChevronRight, lucideArrowRight, lucideLoader
} from '@ng-icons/lucide';

// Shared UI
import {
  PageHeaderComponent, StatusBadgeComponent, InlineAlertComponent,
  SkeletonComponent
} from '@shared/ui/components';

@Component({
  selector: 'app-dashboard-shopee',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule,
    ...HlmCardImports, HlmButton,
    NgIcon, HlmIcon,
    PageHeaderComponent, StatusBadgeComponent, InlineAlertComponent,
    SkeletonComponent,
  ],
  providers: [provideIcons({
    lucideArrowLeft, lucideRefreshCw, lucidePackage, lucideBell, lucideClock,
    lucideCheckCircle, lucideZap, lucideUpload, lucideScanLine, lucideActivity,
    lucideChevronRight, lucideArrowRight, lucideLoader
  })],
  templateUrl: './dashboard-shopee.component.html',
  styleUrl: './dashboard-shopee.component.css'
})
export class DashboardShopeeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Auth observables
  isAdmin$: Observable<boolean>;
  isShopeeDocsTeam$: Observable<boolean>;

  // Loading states
  isLoading = false;
  metricsLoading = true;
  activityLoading = true;
  tasksLoading = true;
  loadingMoreActivity = false;

  // Data
  metrics = {
    totalOrders: 0,
    activePreAlerts: 0,
    systemActivity: 0,
    recentOrders: 0,
    pendingHandovers: 0,
    completedToday: 0
  };

  // Activity and Tasks
  recentActivity: any[] = [];
  pendingTasks: any[] = [];

  // Messages
  successMessage = '';
  errorMessage = '';

  constructor(
    private authService: AuthService,
    private orderService: OrderService,
    private preAlertService: PreAlertService,
    private router: Router
  ) {
    // Initialize auth observables
    this.isAdmin$ = this.authService.isAdmin$;
    this.isShopeeDocsTeam$ = this.authService.isShopeeDocsTeam$;
  }

  ngOnInit(): void {
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all dashboard data
   */
  loadDashboardData(): void {
    this.isLoading = true;
    this.loadMetrics();
    this.loadRecentActivity();
    this.loadPendingTasks();
  }

  /**
   * Refresh all dashboard data
   */
  refreshData(): void {
    this.loadDashboardData();
  }

  /**
   * Load dashboard metrics
   */
  loadMetrics(): void {
    this.metricsLoading = true;

    // Load orders for metrics
    this.orderService.getOrders({ page: 1, pageSize: 100 }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.metricsLoading = false)
    ).subscribe({
      next: (response) => {
        if (response.items) {
          this.metrics.totalOrders = response.totalCount || response.items.length;
          this.metrics.recentOrders = response.items.length;
          this.metrics.systemActivity = response.items.filter(order =>
            order.status === 'Processing' || order.status === 'Ready'
          ).length;

          // Calculate pending handovers (orders ready for handover)
          this.metrics.pendingHandovers = response.items.filter(order =>
            order.status === 'Ready' || order.status === 'Pending Handover'
          ).length;

          // Calculate completed today (orders completed today)
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          this.metrics.completedToday = response.items.filter(order => {
            if (order.status === 'Completed' && order.lastUpdated) {
              const orderDate = new Date(order.lastUpdated);
              orderDate.setHours(0, 0, 0, 0);
              return orderDate.getTime() === today.getTime();
            }
            return false;
          }).length;
        }

        // Load pre-alerts count
        this.preAlertService.getPreAlerts({ page: 1, page_size: 100 }).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (preAlertResponse) => {
            if (preAlertResponse.items) {
              this.metrics.activePreAlerts = preAlertResponse.totalCount || preAlertResponse.items.length;
            }
          },
          error: () => {
            this.metrics.activePreAlerts = 0;
          }
        });
      },
      error: () => {
        this.metrics = {
          totalOrders: 0,
          activePreAlerts: 0,
          systemActivity: 0,
          recentOrders: 0,
          pendingHandovers: 0,
          completedToday: 0
        };
      }
    });
  }

  /**
   * Load recent activity
   */
  loadRecentActivity(): void {
    this.activityLoading = true;

    forkJoin({
      orders: this.orderService.getOrders({ page: 1, pageSize: 10 }).pipe(
        catchError(() => of({ items: [], totalCount: 0, page: 1, pageSize: 0, totalPages: 0 }))
      ),
      preAlerts: this.preAlertService.getPreAlerts({
        page: 1,
        page_size: 10,
        status: '',
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0, pageSize: 0, pageNumber: 0, totalPages: 0 }))
      )
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.activityLoading = false)
    ).subscribe({
      next: (data) => {
        const recentActivity: any[] = [];

        // Add recent orders
        if (data.orders.items && data.orders.items.length > 0) {
          data.orders.items.slice(0, 5).forEach((order: any) => {
            recentActivity.push({
              id: `order-${order.id}`,
              type: 'order',
              title: 'Order Activity',
              description: `Order #${order.shopeeOrderNo || order.id} - Status: ${order.status || 'Pending'}`,
              timestamp: new Date(order.createdAt || new Date()),
              user: 'System'
            });
          });
        }

        // Add recent pre-alerts
        if (data.preAlerts.items && data.preAlerts.items.length > 0) {
          data.preAlerts.items.slice(0, 5).forEach((preAlert: any) => {
            recentActivity.push({
              id: `prealert-${preAlert.ladingBill}`,
              type: 'pre-alert',
              title: 'Pre-Alert Activity',
              description: `Pre-Alert ${preAlert.ladingBill} - ${preAlert.cartonCount || 0} cartons`,
              timestamp: new Date(preAlert.createdAtUtc || new Date()),
              user: 'System'
            });
          });
        }

        // Sort by timestamp (newest first)
        this.recentActivity = recentActivity
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, 10);
      },
      error: () => {
        this.recentActivity = [];
      }
    });
  }

  /**
   * Load pending tasks
   */
  loadPendingTasks(): void {
    this.tasksLoading = true;

    forkJoin({
      orders: this.orderService.getOrders({ page: 1, pageSize: 50 }).pipe(
        catchError(() => of({ items: [], totalCount: 0, page: 1, pageSize: 0, totalPages: 0 }))
      ),
      preAlerts: this.preAlertService.getPreAlerts({
        page: 1,
        page_size: 50,
        status: '',
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0, pageSize: 0, pageNumber: 0, totalPages: 0 }))
      )
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.tasksLoading = false)
    ).subscribe({
      next: (data) => {
        const tasks: any[] = [];

        // Order-based tasks
        if (data.orders.items && data.orders.items.length > 0) {
          const pendingOrders = data.orders.items.filter(order => order.status === 'Pending' || !order.status).length;
          const processingOrders = data.orders.items.filter(order => order.status === 'Processing').length;
          const readyOrders = data.orders.items.filter(order => order.status === 'Ready').length;

          if (pendingOrders > 0) {
            tasks.push({
              id: 1,
              title: 'Process Pending Orders',
              description: `${pendingOrders} order${pendingOrders > 1 ? 's' : ''} awaiting processing`,
              route: '/dashboard/orders',
              priority: 'high'
            });
          }

          if (processingOrders > 0) {
            tasks.push({
              id: 2,
              title: 'Complete Processing Orders',
              description: `${processingOrders} order${processingOrders > 1 ? 's' : ''} currently being processed`,
              route: '/dashboard/orders',
              priority: 'medium'
            });
          }

          if (readyOrders > 0) {
            tasks.push({
              id: 3,
              title: 'Ready for Handover',
              description: `${readyOrders} order${readyOrders > 1 ? 's' : ''} ready for customer handover`,
              route: '/dashboard/orders',
              priority: 'medium'
            });
          }
        }

        // Pre-alert based tasks
        if (data.preAlerts.items && data.preAlerts.items.length > 0) {
          const pendingPreAlerts = data.preAlerts.items.filter(pa => pa.status === 'Pending').length;
          const inTransitPreAlerts = data.preAlerts.items.filter(pa => pa.status === 'InTransit').length;
          const arrivedPreAlerts = data.preAlerts.items.filter(pa => pa.status === 'Arrived').length;

          if (pendingPreAlerts > 0) {
            tasks.push({
              id: 4,
              title: 'Review Pending Pre-Alerts',
              description: `${pendingPreAlerts} pre-alert${pendingPreAlerts > 1 ? 's' : ''} need review`,
              route: '/dashboard/pre-alerts',
              priority: 'medium'
            });
          }

          if (inTransitPreAlerts > 0) {
            tasks.push({
              id: 5,
              title: 'Track In-Transit Shipments',
              description: `${inTransitPreAlerts} shipment${inTransitPreAlerts > 1 ? 's' : ''} currently in transit`,
              route: '/dashboard/pre-alerts',
              priority: 'low'
            });
          }

          if (arrivedPreAlerts > 0) {
            tasks.push({
              id: 6,
              title: 'Process Arrived Shipments',
              description: `${arrivedPreAlerts} shipment${arrivedPreAlerts > 1 ? 's' : ''} have arrived and need processing`,
              route: '/dashboard/pre-alerts',
              priority: 'high'
            });
          }
        }

        this.pendingTasks = tasks;
      },
      error: () => {
        this.pendingTasks = [];
      }
    });
  }

  /**
   * Load more activity items
   */
  loadMoreActivity(): void {
    this.loadingMoreActivity = true;

    forkJoin({
      orders: this.orderService.getOrders({ page: 2, pageSize: 10 }).pipe(
        catchError(() => of({ items: [], totalCount: 0, page: 1, pageSize: 0, totalPages: 0 }))
      ),
      preAlerts: this.preAlertService.getPreAlerts({
        page: 2,
        page_size: 10,
        status: '',
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0, pageSize: 0, pageNumber: 0, totalPages: 0 }))
      )
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loadingMoreActivity = false)
    ).subscribe({
      next: (data) => {
        const moreActivity: any[] = [];

        if (data.orders.items && data.orders.items.length > 0) {
          data.orders.items.slice(0, 3).forEach((order: any) => {
            moreActivity.push({
              id: `order-more-${order.id}`,
              type: 'order',
              title: 'Order Update',
              description: `Order #${order.shopeeOrderNo || order.id} - Status: ${order.status || 'Pending'}`,
              timestamp: new Date(order.updatedAt || order.createdAt || new Date()),
              user: 'System'
            });
          });
        }

        if (data.preAlerts.items && data.preAlerts.items.length > 0) {
          data.preAlerts.items.slice(0, 3).forEach((preAlert: any) => {
            moreActivity.push({
              id: `prealert-more-${preAlert.ladingBill}`,
              type: 'pre-alert',
              title: 'Pre-Alert Update',
              description: `Pre-Alert ${preAlert.ladingBill} - Status: ${preAlert.status}`,
              timestamp: new Date(preAlert.lastUpdatedAtUtc || preAlert.createdAtUtc || new Date()),
              user: 'System'
            });
          });
        }

        this.recentActivity = [...this.recentActivity, ...moreActivity];
      },
      error: () => { /* silent — list already shows prior data */ }
    });
  }

  /**
   * Navigate to specific task
   */
  navigateToTask(task: any): void {
    this.router.navigate([task.route]);
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  /**
   * Track by function for ngFor performance
   */
  trackActivityById(index: number, activity: any): any {
    return activity.id || index;
  }

  /**
   * Clear status messages
   */
  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }
}
